import type { AccountSettingsResponse } from "@/api/accountSettings";

/**
 * UI language resolution for Classwize (CM1 i18n strategy).
 *
 * - i18next uses short bundle codes: `en`, `es`.
 * - All English regional variants (`en-US`, `en-GB`, `en-AU`, …) map to `en` (one translation tree).
 *
 * Preferred UI language comes from {@link AccountSettingsResponse.preferredLocale} (Account Settings API).
 */

export type SupportedI18nLanguage = "en" | "es";

/** Canonical tags persisted in account settings and shown in the language dropdown. */
export const ACCOUNT_LOCALE_EN = "en-US";
export const ACCOUNT_LOCALE_ES = "es-ES";

export interface ResolvedUiLanguage {
	i18nLanguage: SupportedI18nLanguage;
}

function getNavigatorLanguageTag(): string | undefined {
	if (typeof navigator === "undefined" || typeof navigator.language !== "string") {
		return undefined;
	}
	return navigator.language;
}

/**
 * Maps API / profile tags to i18next bundle codes. English regions collapse to `en`.
 * Spanish regions collapse to `es`.
 */
export function normalizeToSupportedI18nLanguage(rawTag: string | undefined | null): SupportedI18nLanguage {
	if (!rawTag || typeof rawTag !== "string") {
		return "en";
	}
	const trimmed = rawTag.trim();
	if (!trimmed) {
		return "en";
	}
	const primary = trimmed.split("-")[0]?.toLowerCase();
	if (primary === "es") {
		return "es";
	}
	if (primary === "en") {
		return "en";
	}
	return "en";
}

/**
 * Maps stored `preferredLocale` to the account settings dropdown value.
 * Spanish (including legacy `es`) maps to {@link ACCOUNT_LOCALE_ES}; i18n still loads `es` bundles.
 */
export function normalizePreferredLocaleForSelect(raw: string | undefined | null): string {
	if (!raw || typeof raw !== "string" || !raw.trim()) {
		return ACCOUNT_LOCALE_EN;
	}
	const trimmed = raw.trim();
	const primary = trimmed.split("-")[0]?.toLowerCase();
	if (primary === "es") {
		return ACCOUNT_LOCALE_ES;
	}
	if (primary === "en") {
		return trimmed.includes("-") ? trimmed : ACCOUNT_LOCALE_EN;
	}
	return ACCOUNT_LOCALE_EN;
}

function pickRawLocaleTag(profilePreferred: string | undefined | null, navigatorTag: string | undefined): string {
	if (profilePreferred && typeof profilePreferred === "string" && profilePreferred.trim()) {
		return profilePreferred.trim();
	}
	if (navigatorTag && navigatorTag.trim()) {
		return navigatorTag.trim();
	}
	return "en-US";
}

export interface ResolveUiLanguageInput {
	/** From account settings `preferredLocale`, or `undefined` to fall back to the browser. */
	profileLocale?: string | null;
	/** Override for tests; defaults to `navigator.language`. */
	navigatorLanguageTag?: string | null;
}

/**
 * Resolves active i18next language for the host and MFE `locale` prop.
 */
export function resolveUiLanguage(input: ResolveUiLanguageInput = {}): ResolvedUiLanguage {
	const nav = input.navigatorLanguageTag ?? getNavigatorLanguageTag();
	const raw = pickRawLocaleTag(input.profileLocale ?? null, nav);
	const i18nLanguage = normalizeToSupportedI18nLanguage(raw);
	return { i18nLanguage };
}

/**
 * Reads `preferredLocale` from loaded {@link AccountSettingsResponse}.
 *
 * - When settings are not yet available (`undefined` / `null`), returns `undefined` so callers can fall back to `navigator.language`.
 * - When settings are loaded but `preferredLocale` is missing or blank, returns `"en"`.
 */
export function readProfileLocaleFromUserInfo(accountSettings: unknown): string | undefined {
	if (accountSettings === null || accountSettings === undefined) {
		return undefined;
	}
	if (typeof accountSettings !== "object") {
		return undefined;
	}
	const record = accountSettings as Partial<AccountSettingsResponse>;
	// API always returns `use24Hour`; use it to distinguish a real settings payload from arbitrary objects.
	if (typeof record.use24Hour !== "boolean") {
		return undefined;
	}
	if (typeof record.preferredLocale === "string" && record.preferredLocale.trim()) {
		return record.preferredLocale.trim();
	}
	return "en";
}
